/**
 * Pacing math: when the campaign dials, how many calls fit in a day, and which
 * ceiling is the one actually limiting it (VOX-270 — Features 4.2 / 4.3).
 *
 * All pure and free of React so it can be tested without a DOM.
 *
 * ⚠️ This file is NOT a backend stand-in: it is real business logic and it stays
 * when the endpoints arrive. What does not belong here are the *rates* — see
 * `services/__drafts__/billingRates.ts`.
 */

import { PROVIDER_DAILY_CAP } from '../services/__drafts__/billingRates';
import { dateTimeFormat } from '../../../utils/intlFormatting';
import type { DayOverride, Pacing, Slot } from '../types';

/**
 * ⚠️ BACKEND GAP, re-exported deliberately.
 *
 * The provider's daily capacity is a draft value (see `__drafts__/README.md`).
 * It is re-exported from here so every consumer in the feature imports it from
 * the domain rather than reaching into `__drafts__` themselves — which leaves
 * exactly ONE import to change when the real capacity arrives over the wire.
 */
export { PROVIDER_DAILY_CAP };

/** The cap a campaign gets when it goes from "no cap" back to capped with no memory. */
export const DEFAULT_CAP = 120;

/* ─── Date helpers ───────────────────────────────────────────────────────── */

/** "YYYY-MM-DD" in local time. `toISOString()` is not used: it shifts to UTC. */
export function toIsoDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** ISO weekday, 1 = Monday … 7 = Sunday (JS gives 0 = Sunday). */
export function isoWeekday(date: Date): number {
  return ((date.getDay() + 6) % 7) + 1;
}

/**
 * The seven weekday initials, Monday first, in the reader's language
 * ("M T W T F S S" / "L M X J V S D").
 *
 * `timeZone: 'UTC'` is not decorative: without it `Intl` formats the UTC date
 * in the local zone, and anywhere west of Greenwich it steps back a day, so the
 * week started on Sunday while the calendar grid is Monday-first.
 */
export function weekdayLabels(locale: string): string[] {
  // The bare language, not `intlLocale`'s region: es-MX's narrow Wednesday is
  // "M" (two Ms in a row); 'es' gives "X", which tells it from Monday.
  const fmt = dateTimeFormat(locale, { weekday: 'narrow', timeZone: 'UTC' });
  // 2024-01-01 was a Monday: seven days from there are the week in order.
  return Array.from({ length: 7 }, (_, i) =>
    fmt.format(new Date(Date.UTC(2024, 0, 1 + i))).toUpperCase(),
  );
}

/**
 * The full weekday name, Monday first — the accessible name of a toggle whose
 * visible label is one letter (two of them read "T").
 */
export function weekdayName(isoDay: number, locale: string): string {
  return dateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' }).format(
    new Date(Date.UTC(2024, 0, isoDay)),
  );
}

/** Parses "YYYY-MM-DD" at local midnight, or null when it isn't a date. */
function parseIsoDate(iso: string): Date | null {
  const date = new Date(`${iso}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

/* ─── Slots ──────────────────────────────────────────────────────────────── */

/** Maximum number of slots. More than three stops reading as a split day. */
export const MAX_SLOTS = 3;

function toMinutes(hhmm: string): number | null {
  const [h, m] = String(hhmm).split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

function toHHMM(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24;
  const m = Math.round(minutes % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * The default name is positional and nothing more.
 *
 * "Morning" / "Afternoon" / "Night" were tried and dropped: a name that
 * promises a schedule it doesn't keep is worse than no name. The slot always
 * travels with the hours it was configured with, and whoever wants to call it
 * "Morning" names it by hand.
 *
 * The positional name is translated, but this engine doesn't know i18next:
 * whoever renders it passes the resolver. The English literal is only a net for
 * calls from a script or a test.
 */
export function slotLabel(slot: Slot, index: number, fallback?: (n: number) => string): string {
  return slot.name?.trim() || (fallback ? fallback(index + 1) : `Slot ${index + 1}`);
}

/**
 * A campaign is born with ONE slot: the whole window, uninterrupted.
 *
 * Splitting the day is the exception (the lunch gap), so it is added when
 * needed instead of coming pre-split into thirds nobody asked for.
 */
export function slotsFromWindow(from: string, to: string): Slot[] {
  const start = toMinutes(from) ?? 0;
  const end = Math.max(start, toMinutes(to) ?? start);
  return [{ id: 'slot-1', from: toHHMM(start), to: toHHMM(end) }];
}

/**
 * Callable minutes an active day offers.
 *
 * Sums the slots rather than the window span, so a lunch gap does subtract
 * capacity. And it merges overlaps before summing: two overlapping slots don't
 * give double the time, they give the same hours.
 */
export function slotMinutes(slots: Slot[]): number {
  const spans = slots
    .map((s) => [toMinutes(s.from), toMinutes(s.to)] as const)
    .filter((x): x is [number, number] => x[0] !== null && x[1] !== null && x[1] > x[0])
    .sort((a, b) => a[0] - b[0]);

  let total = 0;
  let covered = -1;
  for (const [from, to] of spans) {
    const start = Math.max(from, covered);
    if (to > start) total += to - start;
    covered = Math.max(covered, to);
  }
  return total;
}

/**
 * The total call window: from the first hour of the slots to the last.
 *
 * Derived, never configured separately. When the window was its own pair of
 * fields it could contradict the slots that supposedly split it.
 */
export function callWindow(slots: Slot[]): { from: string; to: string } {
  const mins = slots.flatMap((s) =>
    [toMinutes(s.from), toMinutes(s.to)].filter((x): x is number => x !== null),
  );
  if (mins.length === 0) return { from: '00:00', to: '00:00' };
  return { from: toHHMM(Math.min(...mins)), to: toHHMM(Math.max(...mins)) };
}

/**
 * Slots that overlap each other, by id.
 *
 * The PRD forbids saving overlapping slots (Feature 4.2). Returned as a set of
 * the ids involved rather than a boolean so the editor can mark exactly which
 * two rows collide instead of reddening the whole list.
 */
export function overlappingSlotIds(slots: Slot[]): Set<string> {
  const spans = slots
    .map((s) => ({ id: s.id, from: toMinutes(s.from), to: toMinutes(s.to) }))
    .filter((x): x is { id: string; from: number; to: number } => x.from !== null && x.to !== null);

  const out = new Set<string>();
  for (let i = 0; i < spans.length; i += 1) {
    for (let j = i + 1; j < spans.length; j += 1) {
      const a = spans[i];
      const b = spans[j];
      if (a.from < b.to && b.from < a.to) {
        out.add(a.id);
        out.add(b.id);
      }
    }
  }
  return out;
}

/* ─── Daily capacity ─────────────────────────────────────────────────────── */

/**
 * One concrete day's ceiling, from whatever slots that day has.
 *
 * The window only rules in one case: if it doesn't cover a single minute you
 * cannot call, and returning the provider cap there would claim 50,000 calls
 * fit in zero hours.
 */
export function capacityOfSlots(slots: Slot[]): number {
  return slotMinutes(slots) > 0 ? PROVIDER_DAILY_CAP : 0;
}

/** How many contacts fit in an active day under the general slots. */
export function dailyCapacity(pacing: Pacing): number {
  return capacityOfSlots(pacing.slots);
}

/**
 * Which ceiling sets the rate. `both` when the cap equals the window's maximum:
 * raising the cap there changes nothing, and that has to be said.
 */
export type PaceLimit = 'quota' | 'capacity' | 'both';

/** The lesser of the chosen cap and what the window allows, and who rules. */
export function effectivePerDay(pacing: Pacing): {
  perDay: number;
  capacity: number;
  limitedBy: PaceLimit;
} {
  const capacity = dailyCapacity(pacing);
  const quota =
    pacing.contactsPerDay === null ? null : Math.max(0, Math.floor(pacing.contactsPerDay));

  if (quota === null || quota > capacity)
    return { perDay: capacity, capacity, limitedBy: 'capacity' };
  return { perDay: quota, capacity, limitedBy: quota === capacity ? 'both' : 'quota' };
}

/** A daily pace as the runway takes it (`effectivePerDay`'s shape). */
export type DailyPace = ReturnType<typeof effectivePerDay>;

/**
 * The campaign's OWN daily quota, with no provider ceiling — for Performance
 * (VOX-318), which projects a RUNNING campaign and must not lean on the demo
 * `PROVIDER_DAILY_CAP` the wizard still uses until Voice/Channels serve the
 * real capacity.
 *
 * `null` when the campaign runs with no cap: without the lines' real capacity
 * there is no honest rate to project, so the caller says so instead of
 * dividing by a made-up figure. A window with no callable minute still dials
 * nothing (0/day), whatever the quota.
 */
export function ownQuotaPace(pacing: Pacing): DailyPace | null {
  if (pacing.contactsPerDay === null || pacing.contactsPerDay === undefined) return null;
  const quota = slotMinutes(pacing.slots) > 0 ? Math.max(0, Math.floor(pacing.contactsPerDay)) : 0;
  return { perDay: quota, capacity: quota, limitedBy: 'quota' };
}

/* ─── Per-date resolution (the calendar) ─────────────────────────────────── */

/** That date's override, if any. */
export function overrideForDate(pacing: Pacing, iso: string): DayOverride | undefined {
  return pacing.dayOverrides?.[iso];
}

/** True when the date carries any exception, so the calendar can mark it. */
export function hasOverride(pacing: Pacing, iso: string): boolean {
  const o = overrideForDate(pacing, iso);
  if (!o) return false;
  return Boolean(o.skip || o.slots?.length || o.perDay !== undefined || o.slotSubagents);
}

/**
 * The slots that apply to one concrete date, resolving precedence in ONE place:
 *
 *   that date's exception → the general slots of that weekday
 *
 * `null` means the campaign does not dial that day at all (holiday, or a
 * weekday that isn't active).
 *
 * Without this, the calendar would reimplement the precedence and the first
 * time somebody added another level the two readings would drift apart.
 */
export function slotsForDate(pacing: Pacing, iso: string): Slot[] | null {
  const override = overrideForDate(pacing, iso);
  if (override?.skip) return null;
  if (override?.slots?.length) return override.slots;

  const date = parseIsoDate(iso);
  if (!date) return null;
  if (!pacing.days.includes(isoWeekday(date))) return null;

  return pacing.slots;
}

/**
 * How many calls go out on one concrete date.
 *
 * Computed over THAT day's slots, not the general ones: with a per-day schedule
 * or an exception, Thursday and Saturday are not the same, and a calendar
 * painting the same number in every cell would be lying exactly where the
 * person went to look for the difference.
 */
export function callsOnDate(pacing: Pacing, iso: string): number {
  const slots = slotsForDate(pacing, iso);
  if (!slots) return 0;

  const capacity = capacityOfSlots(slots);
  // That day's cap if it has one, the general one otherwise. Compared against
  // `undefined` and not with `??`, because `null` here means something
  // different from "not configured": it is "uncapped this day".
  const own = overrideForDate(pacing, iso)?.perDay;
  const quota = own !== undefined ? own : pacing.contactsPerDay;

  return quota === null ? capacity : Math.min(quota, capacity);
}

/** The dates inside [start, end] the campaign is allowed to dial on. */
export function activeDays(startDate: string, endDate: string | null, days: number[]): string[] {
  if (!startDate || days.length === 0) return [];
  const start = parseIsoDate(startDate);
  if (!start) return [];

  // With no end date the calendar bounds nothing: a year is projected, which is
  // more than any base takes to run out at a sensible rate.
  const end = endDate ? parseIsoDate(endDate) : new Date(start.getTime() + 365 * 24 * 3600 * 1000);
  if (!end || end < start) return [];

  const out: string[] = [];
  const cursor = new Date(start);
  // Hard stop so a mistyped year can't spin the loop.
  while (cursor <= end && out.length < 400) {
    if (days.includes(isoWeekday(cursor))) out.push(toIsoDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

/**
 * The real spread of the audience across the days, not the ceiling repeated.
 *
 * `callsOnDate` answers "how much FITS this day", and with only that the
 * calendar painted 120 in every cell forever: a base of 300 contacts looked
 * identical to one of 30,000. What you go to a calendar for is the opposite:
 * when it runs out, and how much goes out on the last day.
 *
 * So it walks from the start consuming a pool. The last active day gets the
 * remainder (47 instead of 120) and the following ones get zero — exactly the
 * difference between "the campaign runs until December" and "the campaign ends
 * on October 3rd and the rest is empty calendar".
 *
 * `pool` at `null` is "no audience yet": each day's ceiling is returned without
 * spreading, because a zero across the whole view would read as a configuration
 * error rather than an audience nobody has chosen.
 *
 * `until` bounds the walk to the last day the calendar can show: with no end
 * date there is no natural horizon and a large base at one contact a second
 * stretches for years.
 */
export function callPlan(
  pacing: Pacing,
  startDate: string,
  endDate: string | null,
  pool: number | null,
  until: string,
): Map<string, number> {
  const out = new Map<string, number>();
  if (!startDate) return out;

  const start = parseIsoDate(startDate);
  const horizon = parseIsoDate(until);
  if (!start || !horizon) return out;

  const endParsed = endDate && endDate < until ? parseIsoDate(endDate) : horizon;
  const last = endParsed ?? horizon;
  if (last < start) return out;

  let remaining = pool ?? Infinity;
  const cursor = new Date(start);
  // Hard stop: a mistyped year in "until" can't hang the render.
  for (let guard = 0; cursor <= last && guard < 2000; guard += 1) {
    const iso = toIsoDate(cursor);
    const ceiling = callsOnDate(pacing, iso);
    const calls = remaining > 0 ? Math.min(ceiling, remaining) : 0;
    out.set(iso, calls);
    remaining -= calls;
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

/* ─── Retries ────────────────────────────────────────────────────────────── */

/** Retries are capped at 3 by the PRD; the UI offers 0–3. */
export const MAX_RETRIES = 3;

/**
 * Total calls a contact can receive: the retries plus the first attempt.
 *
 * Exists as a function, and not as `maxRetries + 1` written inline, because the
 * PRD requires the hint to state the total, and a sum written inline drifts
 * into two different ways of writing it.
 */
export function totalCallsPerContact(maxRetries: number): number {
  return Math.max(0, maxRetries) + 1;
}
